{
  "entry": {
    "title": "Order Entry",
    "subtitle": "Computerized Provider Order Entry (CPOE)",
    "searchSection": {
      "title": "Search & Add Orders",
      "cosignNotice": "Your orders will require attending cosign before activation."
    },
    "pending": {
      "title": "Pending Orders",
      "clearAll": "Clear All",
      "collapseAria": "Collapse details",
      "expandAria": "Expand details",
      "removeAria": "Remove order",
      "startDateTime": "Start Date / Time",
      "ariaStartDate": "Order start date",
      "ariaStartTime": "Order start time",
      "clinicalIndication": "Clinical Indication",
      "clinicalIndicationPlaceholder": "Reason for ordering…",
      "specialInstructions": "Special Instructions",
      "specialInstructionsPlaceholder": "Additional instructions for the performing department…",
      "diagnosisLabel": "Diagnosis (ICD-10)",
      "diagnosisPlaceholder": "Search ICD-10 diagnosis code…",
      "noActiveEncounter": "No active encounter — orders cannot be placed.",
      "placing": "Placing…",
      "placeAll": "Place All Orders ({{count}})"
    },
    "active": {
      "title": "Active Orders",
      "loading": "Loading orders…",
      "emptyAll": "No orders found for this patient.",
      "emptyFiltered": "No {{type}} orders found.",
      "columns": {
        "orderNumber": "Order #",
        "name": "Name",
        "type": "Type",
        "priority": "Priority",
        "status": "Status",
        "orderedBy": "Ordered By",
        "orderedDate": "Ordered Date",
        "actions": "Actions"
      },
      "external": "External",
      "externalTitle": "External order from {{source}}",
      "externalTitleWithId": "External order from {{source}} (#{{id}})",
      "providerPlaceholder": "Provider #{{id}}",
      "actions": {
        "complete": "Complete",
        "cancel": "Cancel",
        "completeTooltip": "Complete order",
        "cancelTooltip": "Cancel order"
      }
    },
    "cancelDialog": {
      "title": "Cancel Order",
      "prompt": "Please provide a reason for cancelling {{orderName}}.",
      "placeholder": "Cancellation reason (required)…",
      "goBack": "Go Back",
      "confirm": "Confirm Cancel"
    },
    "toast": {
      "cancelSuccess": "Order cancelled successfully",
      "cancelError": "Failed to cancel order",
      "completeSuccess": "Order marked as completed",
      "completeError": "Failed to complete order",
      "noActiveEncounter": "No active encounter found for this patient. Please create an encounter first.",
      "placedSuccess_one": "{{count}} order placed successfully",
      "placedSuccess_other": "{{count}} orders placed successfully",
      "medicationFieldsRequired_one": "{{names}} needs a dose, route and frequency before it can be prescribed",
      "medicationFieldsRequired_other": "{{count}} medications need a dose, route and frequency: {{names}}",
      "medicationNotInFormulary_one": "{{names}} is not linked to the formulary and cannot be screened for allergies, interactions or dose — prescribe it from the medications page",
      "medicationNotInFormulary_other": "{{count}} medications are not linked to the formulary and cannot be screened: {{names}} — prescribe them from the medications page",
      "placedError_one": "{{count}} order failed to place",
      "placedError_other": "{{count}} orders failed to place",
      "orderSetApplied": "Added {{count}} orders from {{name}} — review before signing"
    }
  },
  "catalog": "Order Catalog",
  "orderedBy": "Ordered By",
  "orderedDate": "Ordered Date",
  "scheduledDate": "Scheduled Date",
  "completedDate": "Completed Date",
  "lab": "Laboratory",
  "imaging": "Imaging",
  "medication": "Medication",
  "procedure": "Procedure",
  "consultation": "Consultation",
  "priority": {
    "label": "Priority",
    "values": {
      "stat": "STAT",
      "asap": "ASAP",
      "urgent": "Urgent",
      "routine": "Routine",
      "scheduled": "Scheduled",
      "prn": "PRN"
    }
  },
  "__review_priority": "STAT / ASAP / PRN are clinical acronyms kept verbatim across all locales per Phase-2 register policy (Task 3 M-1). Only `urgent`, `routine`, `scheduled` translate. Phase-0 seeds that softened STAT to '紧急 / 至急 / CẤP CỨU' have been hardened.",
  "status": {
    "label": "Order Status",
    "values": {
      "draft": "Draft",
      "pending": "Pending",
      "pendingCosign": "Pending Cosign",
      "signed": "Signed",
      "sent": "Sent",
      "active": "Active",
      "ordered": "Ordered",
      "inProgress": "In Progress",
      "holdForVerify": "Hold for Verification",
      "completed": "Completed",
      "cancelled": "Cancelled",
      "discontinued": "Discontinued",
      "archived": "Archived",
      "pendingClarification": "Pending Clarification",
      "suspended": "Suspended",
      "unknown": "Unknown"
    }
  },
  "entryFields": {
    "searchPlaceholder": "Search orders (labs, imaging, meds, procedures)…",
    "frequency": "Frequency",
    "dose": "Dose",
    "route": "Route",
    "indication": "Indication",
    "duration": "Duration",
    "comments": "Comments",
    "signOrder": "Sign Order",
    "holdOrder": "Hold Order",
    "frequencyOptions": {
      "stat": "STAT",
      "prn": "PRN",
      "bid": "BID",
      "tid": "TID",
      "qid": "QID",
      "qd": "QD",
      "qhs": "QHS",
      "ac": "AC",
      "pc": "PC"
    }
  },
  "catalogPage": {
    "title": "Order Catalog",
    "searchPlaceholder": "Search orders by name, code, or synonym…",
    "categories": "Categories",
    "favorites": "Favorites",
    "recentlyUsed": "Recently Used",
    "restricted": "Restricted",
    "searchCatalog": "Search Catalog",
    "tabs": {
      "all": "All",
      "lab": "Lab",
      "imaging": "Imaging",
      "medication": "Medication",
      "procedure": "Procedure",
      "consultation": "Consultation"
    },
    "columns": {
      "orderName": "Order Name",
      "code": "Code",
      "category": "Category",
      "description": "Description",
      "defaultPriority": "Default Priority"
    },
    "details": {
      "orderCode": "Order Code",
      "category": "Category",
      "defaultPriority": "Default Priority",
      "status": "Status",
      "cptCode": "CPT Code",
      "loincCode": "LOINC Code",
      "synonyms": "Synonyms",
      "description": "Description",
      "active": "Active",
      "inactive": "Inactive"
    },
    "loading": "Loading catalog…",
    "loadError": "Failed to load order catalog",
    "emptyResults": "No orders found matching your criteria.",
    "pagination": "Showing {{from}}-{{to}} of {{total}}",
    "pageIndicator": "Page {{page}} of {{totalPages}}",
    "favoriteAdd": "Add to favorites",
    "favoriteRemove": "Remove from favorites",
    "favoriteAdded": "Added to favorites",
    "favoriteRemoved": "Removed from favorites"
  },
  "cosign": {
    "title": "Order Cosign Queue",
    "requiresCosign": "Requires Cosign",
    "cosignedBy": "Cosigned By",
    "cosignDeadline": "Cosign Deadline",
    "queueEmpty": "No orders require cosigning.",
    "pendingCount": "{{count}} pending",
    "loading": "Loading cosign queue…",
    "loadError": "Failed to load cosign queue.",
    "columns": {
      "patient": "Patient",
      "order": "Order",
      "type": "Type",
      "orderingProvider": "Ordering Provider",
      "priority": "Priority",
      "orderedDate": "Ordered Date",
      "action": "Action"
    },
    "actions": {
      "cosign": "Cosign"
    },
    "toast": {
      "success": "Order cosigned successfully",
      "error": "Failed to cosign order"
    },
    "cosigningAs": "Cosigning as: {{name}}"
  },
  "sets": {
    "title": "Order Sets",
    "newOrderSet": "New Order Set",
    "addItem": "Add Item",
    "requiredItem": "Required",
    "optionalItem": "Optional",
    "editMode": "Edit Mode",
    "published": "Published",
    "draft": "Draft",
    "archived": "Archived",
    "versionNotes": "Version Notes",
    "createTitle": "Create New Order Set",
    "fields": {
      "name": "Name",
      "category": "Category",
      "specialty": "Specialty",
      "description": "Description"
    },
    "placeholders": {
      "name": "e.g., Chest Pain Workup",
      "categorySelect": "Select category…",
      "specialty": "e.g., Emergency Medicine",
      "description": "Brief description…",
      "search": "Search order sets…"
    },
    "categories": {
      "chestPain": "Chest Pain",
      "sepsis": "Sepsis",
      "postOp": "Post-Op",
      "admission": "Admission",
      "discharge": "Discharge",
      "other": "Other"
    },
    "statusFilter": {
      "all": "All Statuses",
      "active": "Active",
      "draft": "Draft",
      "archived": "Archived"
    },
    "columns": {
      "name": "Name",
      "category": "Category",
      "specialty": "Specialty",
      "items": "Items",
      "status": "Status",
      "version": "Version",
      "actions": "Actions"
    },
    "itemColumns": {
      "seq": "Seq",
      "type": "Type",
      "item": "Item",
      "priority": "Priority",
      "required": "Required"
    },
    "itemTypes": {
      "order": "Order",
      "sectionHeader": "Section Header",
      "instruction": "Instruction"
    },
    "actions": {
      "publish": "Publish",
      "archive": "Archive",
      "cancel": "Cancel",
      "create": "Create",
      "removeItem": "Remove item"
    },
    "loading": "Loading order sets…",
    "loadError": "Failed to load order sets",
    "emptyResults": "No order sets found.",
    "expandLoading": "Loading items…",
    "expandEmpty": "No items in this order set.",
    "validationRequired": "Name, category, and specialty are required",
    "toast": {
      "created": "Order set created",
      "createError": "Failed to create order set",
      "published": "Order set published",
      "publishError": "Failed to publish order set",
      "archived": "Order set archived",
      "archiveError": "Failed to archive order set",
      "itemRemoved": "Item removed",
      "itemRemoveError": "Failed to remove item"
    },
    "versionBadge": "v{{version}}"
  },
  "search": {
    "placeholder": "Search orders (labs, imaging, meds, procedures)…",
    "noResults": "No orders found for \"{{query}}\"",
    "categories": {
      "all": "All",
      "lab": "Lab",
      "imaging": "Imaging",
      "med": "Med",
      "procedure": "Procedure",
      "consultation": "Consultation"
    },
    "addAll": "Add all {{count}}"
  },
  "worklist": {
    "title": "Orders Worklist",
    "countBadge": "{{count}} orders",
    "loading": "Loading orders…",
    "loadError": "Could not load orders. Please try again.",
    "empty": "No orders match the current filters.",
    "typeLabel": "Order Type",
    "allOption": "All",
    "columns": {
      "patient": "Patient",
      "order": "Order",
      "type": "Type",
      "priority": "Priority",
      "status": "Status",
      "ordered": "Ordered"
    }
  }
}
